import { useEffect, useState } from "react";
import { Save, Trash2, Plus, RotateCcw, FileText, ChevronDown, ChevronUp } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SLUG_LABELS = {
  privacy: "Privacy & GDPR",
  "algemene-voorwaarden": "Algemene voorwaarden",
  cookiebeleid: "Cookiebeleid",
};

export default function AdminLegalPages() {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const r = await api.get("/admin/legal-pages/");
    setPages(r.data.pages || []);
    if (r.data.pages?.length) {
      setActiveSlug((prev) => prev || r.data.pages[0].slug);
      setDraft((prev) => prev || structuredClone(r.data.pages[0]));
    }
  }
  useEffect(() => { load(); }, []);

  function selectPage(slug) {
    setActiveSlug(slug);
    const p = pages.find((x) => x.slug === slug);
    if (p) setDraft(structuredClone(p));
  }

  function updateField(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
  }
  function updateSection(idx, key, v) {
    setDraft((d) => {
      const sections = [...(d.sections || [])];
      sections[idx] = { ...sections[idx], [key]: v };
      return { ...d, sections };
    });
  }
  function addSection() {
    setDraft((d) => ({ ...d, sections: [...(d.sections || []), { heading: "Nieuwe sectie", body_html: "<p>…</p>" }] }));
  }
  function removeSection(idx) {
    if (!confirm("Verwijder sectie?")) return;
    setDraft((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== idx) }));
  }
  function moveSection(idx, dir) {
    setDraft((d) => {
      const sections = [...d.sections];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= sections.length) return d;
      [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
      return { ...d, sections };
    });
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/legal-pages/${activeSlug}`, {
        title: draft.title,
        eyebrow: draft.eyebrow,
        intro: draft.intro,
        last_updated_label: draft.last_updated_label,
        sections: draft.sections,
      });
      toast({ title: "Opgeslagen", description: `${SLUG_LABELS[activeSlug] || activeSlug} is bijgewerkt.` });
      load();
    } catch (err) {
      toast({ title: "Mislukt", description: err?.message || "Probeer opnieuw", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function resetToDefault() {
    if (!confirm(`Reset "${SLUG_LABELS[activeSlug] || activeSlug}" naar de fabrieksinhoud? Je huidige wijzigingen gaan verloren.`)) return;
    await api.post(`/admin/legal-pages/${activeSlug}/reset`);
    toast({ title: "Reset uitgevoerd" });
    load();
  }

  if (!draft) {
    return <AdminLayout><div className="h-8 w-1/3 bg-slate-200 rounded animate-pulse" /></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-legal-pages">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight flex items-center gap-3">
              <FileText className="h-7 w-7 text-[#002FA7]" /> Juridische pagina&rsquo;s
            </h1>
            <p className="text-ink-soft mt-1 text-sm max-w-2xl">
              Bewerk Privacy, Algemene voorwaarden en Cookiebeleid. Wijzigingen zijn direct live na opslaan. Reset altijd mogelijk naar fabrieksinhoud.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={resetToDefault} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border hover:bg-secondary text-ink text-sm font-semibold" data-testid="reset-page">
              <RotateCcw className="h-4 w-4" /> Reset naar default
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold disabled:opacity-50" data-testid="save-page">
              <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Bewaar wijzigingen"}
            </button>
          </div>
        </div>

        {/* Page selector */}
        <div className="flex flex-wrap gap-2" data-testid="page-tabs">
          {pages.map((p) => (
            <button
              key={p.slug}
              onClick={() => selectPage(p.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeSlug === p.slug ? "bg-[#002FA7] text-white" : "bg-white border border-border text-ink hover:bg-secondary"}`}
              data-testid={`tab-${p.slug}`}
            >
              {SLUG_LABELS[p.slug] || p.slug}
            </button>
          ))}
        </div>

        {/* Editor */}
        <section className="rounded-2xl bg-white border border-border p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Eyebrow (klein label boven titel)</Label>
              <Input value={draft.eyebrow || ""} onChange={(e) => updateField("eyebrow", e.target.value)} className="mt-1" data-testid="field-eyebrow" />
            </div>
            <div>
              <Label>Slug (niet wijzigbaar)</Label>
              <Input value={draft.slug} disabled className="mt-1 bg-slate-50" />
            </div>
          </div>
          <div>
            <Label>Titel</Label>
            <Input value={draft.title || ""} onChange={(e) => updateField("title", e.target.value)} className="mt-1" data-testid="field-title" />
          </div>
          <div>
            <Label>&ldquo;Laatst bijgewerkt&rdquo; label (vrij tekstveld, bv. &ldquo;Laatst bijgewerkt: 9 juni 2026&rdquo;)</Label>
            <Input value={draft.last_updated_label || ""} onChange={(e) => updateField("last_updated_label", e.target.value)} className="mt-1" data-testid="field-last-updated" />
          </div>
          <div>
            <Label>Intro (kort, 1-3 zinnen)</Label>
            <textarea
              value={draft.intro || ""}
              onChange={(e) => updateField("intro", e.target.value)}
              rows={3}
              className="w-full mt-1 p-3 border border-input rounded-md text-sm"
              data-testid="field-intro"
            />
          </div>
        </section>

        {/* Sections editor */}
        <section className="space-y-4" data-testid="sections-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold text-ink">Secties ({draft.sections?.length || 0})</h2>
            <button onClick={addSection} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold" data-testid="add-section">
              <Plus className="h-3.5 w-3.5" /> Sectie toevoegen
            </button>
          </div>
          {(draft.sections || []).map((s, i) => (
            <article key={i} className="rounded-2xl bg-white border border-border p-5" data-testid={`section-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Sectie #{i + 1}</span>
                <div className="flex gap-1">
                  <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => moveSection(i, 1)} disabled={i === (draft.sections.length - 1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                  <button onClick={() => removeSection(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Hoofding (H2)</Label>
                  <Input value={s.heading} onChange={(e) => updateSection(i, "heading", e.target.value)} className="mt-1" data-testid={`section-${i}-heading`} />
                </div>
                <div>
                  <Label>Inhoud (HTML — gebruik &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;, &lt;a href=&quot;…&quot;&gt;)</Label>
                  <textarea
                    value={s.body_html}
                    onChange={(e) => updateSection(i, "body_html", e.target.value)}
                    rows={8}
                    className="w-full mt-1 p-3 border border-input rounded-md text-xs font-mono"
                    data-testid={`section-${i}-body`}
                  />
                </div>
                <details className="mt-2">
                  <summary className="text-xs text-ink-soft cursor-pointer hover:text-ink">Live preview</summary>
                  <div className="mt-2 p-4 bg-slate-50 rounded-lg prose prose-sm max-w-none border border-border" dangerouslySetInnerHTML={{ __html: s.body_html }} />
                </details>
              </div>
            </article>
          ))}
        </section>
      </div>
    </AdminLayout>
  );
}
